import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { change } from "redux-form";

import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import MuiToolbar from "@material-ui/core/Toolbar";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import MuiList from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";

import CartIcon from "mdi-material-ui/Cart";
import ClipboardListIcon from "mdi-material-ui/ClipboardList";
import CloseCircleIcon from "mdi-material-ui/ProgressAlert";
import AlertIcon from "mdi-material-ui/Alert";
//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/chart/gauge";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { WithReduxForm, ReferenceInput, AutoCompleteInput, DateInput } from "jazasoft";
import Table from "jazasoft/lib/mui/components/Table";
import CardHeader from "../../components/CardHeader";

import { DataType, getSummaryEchartOptions } from "../../helpers/reportHelper";
import { toFixed, getWeightedMean, numberWithCommas, isEmpty } from "../../utils/helpers";
import { colorSecondary, colorTomatoRed, colorLightGreen, colorLemom, colorOrange } from "../../utils/colors";
import { PART_SHELL } from "../../constants";
import { makeStyles } from "@material-ui/core";
import { useDeepMemo, useQuery, useUnit } from "../../hooks";
import { OState } from "../../utils/types";
import { format, subDays } from "date-fns";

const categoryList = [
  "Solid: Normal",
  "Solid: CSV",
  "Up to 1 cm",
  "1 to 2 cm",
  "Above 2 cm",
  "Up to (2x2) cm",
  "(2x2) to (4x4) cm",
  "Above (4x4) cm"
];

const getMarkerEfficiecyReport = reportList => {
  let markerEfficiencyReport = {}; // category -> {efficiency: , fabric: }

  ["Solid", "Stripes", "Checks"].forEach(fabricPattern => {
    if (fabricPattern === "Checks") {
      const breakPoints = [0, 4, 16];
      for (let i = 0; i < breakPoints.length; i++) {
        const lowerLimit = breakPoints[i];
        const upperLimit = i + 1 < breakPoints.length ? breakPoints[i + 1] : Infinity;
        const list = reportList.filter(
          e => e.repeatLength && e.repeatWidth && e.repeatLength * e.repeatWidth >= lowerLimit && e.repeatLength * e.repeatWidth < upperLimit
        );
        let labelSuffix = i === 0 ? "Up to (2x2) cm" : i === 1 ? "(2x2) to (4x4) cm" : "Above (4x4) cm";
        const label = labelSuffix;
        const efficiency = getWeightedMean(list.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned })));
        const fabric = list.reduce((acc, el) => acc + (el.allocated - el.returned), 0);
        markerEfficiencyReport[label] = { efficiency, fabric, poCount: list.length };
      }
    } else if (fabricPattern === "Stripes") {
      const breakPoints = [0, 1, 2];
      for (let i = 0; i < breakPoints.length; i++) {
        const lowerLimit = breakPoints[i];
        const upperLimit = i + 1 < breakPoints.length ? breakPoints[i + 1] : Infinity;
        const list = reportList.filter(
          e =>
            ((e.repeatLength && !e.repeatWidth) || (!e.repeatLength && e.repeatWidth)) &&
            (e.repeatLength ? e.repeatLength >= lowerLimit && e.repeatLength < upperLimit : e.repeatWidth >= lowerLimit && e.repeatWidth < upperLimit)
        );
        let labelSuffix = i === 0 ? "Up to 1 cm" : i === 1 ? "1 to 2 cm" : "Above 2 cm";
        const label = labelSuffix;
        const efficiency = getWeightedMean(list.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned })));
        const fabric = list.reduce((acc, el) => acc + (el.allocated - el.returned), 0);
        markerEfficiencyReport[label] = { efficiency, fabric, poCount: list.length };
      }
    } else {
      let list = reportList.filter(e => !(e.repeatLength || e.repeatWidth) && e.fabricPattern !== "Stripes" && (isEmpty(e.csv) || e.csv === "No"));

      let label = "Solid: Normal";
      let efficiency = getWeightedMean(list.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned })));
      let fabric = list.reduce((acc, el) => acc + (el.allocated - el.returned), 0);
      markerEfficiencyReport[label] = { efficiency, fabric, poCount: list.length };

      list = reportList.filter(e => !(e.repeatLength || e.repeatWidth) && e.fabricPattern !== "Stripes" && !isEmpty(e.csv) && e.csv !== "No");

      label = "Solid: CSV";
      efficiency = getWeightedMean(list.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned })));
      fabric = list.reduce((acc, el) => acc + (el.allocated - el.returned), 0);
      markerEfficiencyReport[label] = { efficiency, fabric, poCount: list.length };
    }
  });
  return markerEfficiencyReport;
};

const getMarkerEfficiencyOptions = markerEfficiencyReport => {
  let options = {};
  const colorList = [
    colorLemom.main,
    colorLemom.main,
    colorSecondary.main,
    colorSecondary.main,
    colorSecondary.main,
    colorOrange.main,
    colorOrange.main,
    colorOrange.main
  ];
  options["barChart"] = {
    color: [colorSecondary.main],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow"
      }
    },
    xAxis: [
      {
        type: "category",
        data: Object.keys(markerEfficiencyReport),
        axisLabel: {
          rotate: 35,
          fontSize: 11
        }
      }
    ],
    yAxis: [
      {
        type: "value",
        show: false,
        axisLabel: {
          formatter: "{value}%"
        }
      }
    ],
    series: [
      {
        name: "efficiency",
        type: "bar",
        barWidth: 20,
        data: Object.keys(markerEfficiencyReport).map(key => toFixed(markerEfficiencyReport[key].efficiency, 1)),
        itemStyle: {
          normal: {
            color: function (params) {
              return colorList[params.dataIndex];
            },

            label: {
              show: true,
              position: "top",
              formatter: "{c}"
            }
          }
        }
      }
    ]
  };

  const efficiency = getWeightedMean(
    Object.keys(markerEfficiencyReport)
      .map(key => markerEfficiencyReport[key])
      .map(e => ({ key: e.efficiency, weight: e.fabric }))
  );

  options["gaugeChart"] = {
    tooltip: {
      formatter: "{a} <br/>{b} : {c}%"
    },
    series: [
      {
        name: "Efficiency",
        type: "gauge",
        min: 50,
        max: 100,
        splitNumber: 5, // Split the number of segments, the default is 5
        axisLine: {
          lineStyle: {
            color: [
              [0.5, colorTomatoRed.main],
              [0.8, colorSecondary.main],
              [1, colorLightGreen.main]
            ],
            width: 4
          }
        },
        axisTick: {
          splitNumber: 10, // How many segments per split
          length: 10, // Attribute length control line length
          lineStyle: {
            color: "auto"
          }
        },
        axisLabel: {
          textStyle: {
            color: "auto",
            fontSize: 10
          }
        },
        splitLine: {
          show: true, // The default display, the property show control is displayed or not
          length: 15, // Attribute length control line length
          lineStyle: {
            color: "auto"
          }
        },
        pointer: {
          width: 5
        },
        detail: {
          formatter: "{value}%",
          textStyle: {
            color: "auto",
            fontWeight: "bold",
            fontSize: 16
          },
          offsetCenter: [0, "60%"]
        },
        data: [{ value: toFixed(efficiency, 1) }]
      }
    ]
  };
  return options;
};

const List = ({ data, rightColor, dense = false }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "1em" }}>
        <Typography>No record available</Typography>
      </div>
    );
  }
  return (
    <MuiList dense={dense}>
      {data.map(({ orderId, style, color, title, subTitle, rightTitle }, idx) => (
        <ListItem
          key={idx}
          divider
          onClick={_ => window.open(`/reports/micro/reconciliation/${orderId}/view?style=${style}&color=${color}`, "_blank")}>
          <ListItemText primary={title} secondary={subTitle ? subTitle : null} />
          <ListItemSecondaryAction>
            <Typography variant="h6" style={{ color: rightColor }}>
              {rightTitle}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </MuiList>
  );
};

const prepareSearchParam = (values = {}, resource = "summary") => {
  const { duration, customerId, seasonId, productId, unitId, from, to } = values;
  if (isEmpty(duration) && isEmpty(seasonId)) return "";

  let items = [];
  if (isEmpty(seasonId) && !isEmpty(duration)) {
    const start = moment(from).startOf("day").valueOf();
    const end = moment(to).endOf("day").valueOf();

    items.push(`orderCloseDate=ge=${start}`);
    items.push(`orderCloseDate=le=${end}`);
  }
  if (!isEmpty(customerId)) {
    items.push(`customerId=="${customerId}"`);
  }
  if (!isEmpty(seasonId)) {
    items.push(`seasonId=="${seasonId}"`);
  }

  if (!isEmpty(productId)) {
    items.push(`productId=="${productId}"`);
  }

  if (!isEmpty(unitId)) {
    if (resource === "summary") {
      items.push(`unitIds=="*${unitId}*"`);
    } else {
      items.push(`unitId=="${unitId}"`);
    }
  }

  if (resource !== "summary") {
    items.push(`state=="Closed"`);
  }

  items.push(`referencePart=='TRUE'`);

  return items.join(";");
};

const defaultValues = {
  duration: "1-weeks"
};

const useStyles = makeStyles(theme => ({
  root: {},
  header: {
    margin: "1em 1.5em"
  },
  content: {
    margin: "1.5em"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  title: {
    flex: "0 0 auto"
  },
  spacer: {
    flexGrow: "1"
  },
  chip: {
    margin: 0,
    padding: 0
  },
  nowrapTable: {
    whiteSpace: "nowrap"
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200
  },
  menu: {
    width: 200
  }
}));

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const filterValues = useSelector(state => state.form["dashboard-filter"] && state.form["dashboard-filter"].values);
  const summarySearch = prepareSearchParam(filterValues, "summary");
  const detailSearch = prepareSearchParam(filterValues, "detail");

  const { unitChoices } = useUnit();

  React.useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = subDays(today, 7);

    dispatch(change("dashboard-filter", "from", format(sevenDaysAgo, "yyyy-MM-dd"), null));
    dispatch(change("dashboard-filter", "to", format(today, "yyyy-MM-dd"), null));
  }, [dispatch]);

  const { data: settings = [] } = useQuery({ url: "settings", params: { search: "groupId==report_alert", size: 1000 } });
  const { data: pendingFlowList = [] } = useQuery({
    url: "order-flow",
    params: {
      search: `type=='CUTPLAN';referencePart=='TRUE';state=in=('${OState.REMNANT_FEEDBACK_COMPLETED.state}', '${OState.FEEDBACK_COMPLETED.state}')${
        !isEmpty(filterValues?.customerId) ? `;order.customer.id=="${filterValues?.customerId}"` : ""
      }${!isEmpty(filterValues?.seasonId) ? `;order.season.id=="${filterValues?.seasonId}"` : ""}${
        !isEmpty(filterValues?.productId) ? `;product.id=="${filterValues?.productId}"` : ""
      }${!isEmpty(filterValues?.unitId) ? `;unitId=="${filterValues?.unitId}"` : ""}`,
      size: 10000
    }
  });
  const { data: reportSummaryList = [] } = useQuery(!isEmpty(summarySearch) && { url: `report-summary`, params: { search: summarySearch } });
  const { data: reportDetailList = [] } = useQuery(!isEmpty(detailSearch) && { url: `report-detail`, params: { search: detailSearch } });

  const [category, setCategory] = useState("Solid: Normal");

  const onChange = (values, dispatch, formProps, previousValues) => {
    if (values.seasonId) {
      dispatch(formProps.change("duration", null));
    } else {
      dispatch(formProps.change("duration", values.duration || defaultValues.duration));
    }
  };

  const renderMarkerEfficiency = () => {
    const markerEfficiencyReport = getMarkerEfficiecyReport(reportSummaryList);

    const options = getMarkerEfficiencyOptions(markerEfficiencyReport);

    return (
      <Grid item xs={12} sm={6}>
        <Card className={classes.border}>
          <CardHeader title="Marker Efficiency" onClick={_ => window.open("/reports/macro/markerEfficiency", "_blank")} />

          <CardContent style={{ padding: "0 1em 1em 1em" }}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item xs={8}>
                <ReactEchartsCore
                  echarts={echarts}
                  option={options["barChart"] || {}}
                  style={{ height: "235px", width: "100%" }}
                  opts={{ renderer: "svg" }}
                  className="react_for_echarts"
                />
              </Grid>
              <Grid item xs={4}>
                <ReactEchartsCore
                  echarts={echarts}
                  option={options["gaugeChart"] || {}}
                  style={{ height: "190px", width: "100%" }}
                  opts={{ renderer: "svg" }}
                  className="react_for_echarts"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // const renderCardHeader = (primaryText, secondaryText, path) => {
  //   return (
  //     <MuiToolbar>
  //       <div className={classes.title}>
  //         <CardHeader title={primaryText} style={{ margin: 0, padding: 0 }} onClick={_ => window.open(path, "_blank")} />
  //       </div>
  //       <div className={classes.spacer} />
  //       <div className={classes.actions}>
  //         <Typography className={classes.chip}>{secondaryText}</Typography>
  //       </div>
  //     </MuiToolbar>
  //   );
  // };

  const options = useDeepMemo(() => {
    if (isEmpty(reportSummaryList)) return {};
    return getSummaryEchartOptions({
      reportList: reportSummaryList,
      dataTypeList: [
        DataType.FABRIC_UTILIZATION_SUMMARY,
        DataType.WASTAGE_SUMMMARY,
        DataType.HIGH_WASTAGE,
        DataType.CONSUMPTION_SUMMARY,
        DataType.INCREASE_IN_CONSUMPTION,
        DataType.DECREASE_IN_CONSUMPTION,
        DataType.LOW_EFFICIENCY,
        DataType.WIDTH_DEVIATION,
        DataType.LENGTH_VARIATION,
        DataType.CUT_ANALYSIS,
        DataType.LOW_CUT_PERCENT
      ],
      settings,
      rowCount: 6
    });
  }, [reportSummaryList, settings]);

  const summaryData = useDeepMemo(() => {
    const poCount = reportDetailList?.length;
    const totalRejectedQty = reportSummaryList.filter(e => e.part === PART_SHELL).reduce((acc, el) => acc + el.rejectedQty, 0);
    const totalCutQty = reportSummaryList.filter(e => e.part === PART_SHELL).reduce((acc, el) => acc + el.cutQty, 0) - (totalRejectedQty || 0);
    const totalOrderQty = reportSummaryList.filter(e => e.part === PART_SHELL).reduce((acc, el) => acc + el.orderQty, 0);

    const cutPercentage = totalOrderQty > 0 ? toFixed((totalCutQty * 100) / totalOrderQty, 1) : 0;
    const totalUnutilized = reportSummaryList.reduce((acc, el) => acc + el.unused, 0);
    return [
      {
        label: "Cut Orders Closed",
        value: numberWithCommas(poCount),
        secondValue: null,
        icon: <CartIcon style={{ fontSize: 48, color: colorLightGreen.main }} />
      },
      {
        label: "Net Cut Quantity",
        value: numberWithCommas(totalCutQty),
        secondValue: `(${cutPercentage}%)`,
        icon: <ClipboardListIcon style={{ fontSize: 48, color: colorSecondary.main }} />
      },
      {
        label: "Cut Order Closer Pending",
        value: pendingFlowList.length,
        secondValue: null,
        icon: <CloseCircleIcon style={{ fontSize: 48, color: colorTomatoRed.main }} />
      },
      {
        label: "Unutilized Fabric",
        value: numberWithCommas(toFixed(totalUnutilized, 0)),
        secondValue: null,
        icon: <AlertIcon style={{ fontSize: 48, color: colorLemom.main }} />
      }
    ];
  }, [pendingFlowList, reportDetailList, reportSummaryList]);

  const onCardClick = label => {
    if (label === "Cut Order Closer Pending") {
      window.open(`/reports/micro/closurePendingReport`, "_blank");
    }
  };

  const inputOptions = {
    xs: 12,
    fullWidth: true,
    options: { fullWidth: true }
  };

  return (
    <div>
      {/* Header */}
      <WithReduxForm form="dashboard-filter" onChange={onChange} initialValues={defaultValues}>
        {() => (
          <div className={classes.header}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Dashboard
                </Typography>
              </Grid>

              <Grid item xs={12} sm={10}>
                <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
                  <Grid item xs={12} sm={2}>
                    <AutoCompleteInput
                      addLabel={false}
                      source="unitId"
                      label="Unit"
                      choices={unitChoices}
                      allowEmpty
                      options={{ fullWidth: true, placeholder: "Unit" }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <ReferenceInput
                      addLabel={false}
                      label="Product"
                      resource="products"
                      source="productId"
                      reference="products"
                      options={{ fullWidth: true, placeholder: "Product" }}
                      sort={{ field: "name", order: "asc" }}
                      allowEmpty>
                      <AutoCompleteInput />
                    </ReferenceInput>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <ReferenceInput
                      addLabel={false}
                      source="seasonId"
                      reference="seasons"
                      options={{ fullWidth: true, placeholder: "Season" }}
                      allowEmpty>
                      <AutoCompleteInput />
                    </ReferenceInput>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <ReferenceInput
                      addLabel={false}
                      source="customerId"
                      reference="customers"
                      options={{ fullWidth: true, placeholder: "Customer" }}
                      allowEmpty>
                      <AutoCompleteInput />
                    </ReferenceInput>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <DateInput label="Start Date" addLabel={false} source="from" {...inputOptions} />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <DateInput label="End Date" addLabel={false} source="to" {...inputOptions} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        )}
      </WithReduxForm>

      {/* Content */}
      {reportSummaryList.length === 0 && (
        <div className={classes.displayCenter}>
          <Typography>Data not available</Typography>
        </div>
      )}

      {reportSummaryList.length > 0 && (
        <div className={classes.content}>
          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} sm={4}>
              <Grid container spacing={2}>
                {summaryData.map(({ label, value, secondValue, icon }, idx) => (
                  <Grid key={idx} item xs={6}>
                    <Card>
                      <div
                        style={{
                          padding: "3em 1.5em",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: 147
                        }}
                        onClick={() => onCardClick(label)}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between"
                            // alignItems: "center"
                          }}>
                          <div>
                            <Typography variant="h4" style={{ textAlign: "left" }}>
                              {value}
                            </Typography>
                          </div>
                          <div>{icon}</div>
                        </div>
                        <div>
                          <Typography color="textSecondary" style={{ textAlign: "left" }}>
                            {secondValue}
                          </Typography>
                        </div>
                        <div>
                          <Typography color="textSecondary" style={{ textAlign: "left" }}>
                            {label}
                          </Typography>
                        </div>
                      </div>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Fabric Utilization Summary" onClick={_ => window.open("/reports/macro/reconciliation", "_blank")} />
                <CardContent style={{ padding: "0 0 8px 0" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options[DataType.FABRIC_UTILIZATION_SUMMARY]}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Wastage Summary" onClick={_ => window.open("/reports/macro/wastage", "_blank")} />
                <CardContent style={{ padding: "0 0 8px 0" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options[DataType.WASTAGE_SUMMMARY]}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader
                  title="Cut Percent Deviation"
                  // onClick={_ => window.open("/reports/macro/cutAnalysis", "_blank")}
                />
                <CardContent style={{ padding: "0 0 8px 16px" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options[DataType.CUT_ANALYSIS] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Low Cut Percent" />
                <Divider />
                <CardContent style={{ padding: 0, height: 258 }}>
                  <PerfectScrollbar>
                    <List data={options[DataType.LOW_CUT_PERCENT] || []} rightColor={colorTomatoRed.main} />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="High Wastage" />
                <Divider />
                <CardContent style={{ padding: 0, height: 258 }}>
                  <PerfectScrollbar>
                    <List data={options[DataType.HIGH_WASTAGE] || []} rightColor={colorTomatoRed.main} />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Consumption Summary" onClick={_ => window.open("/reports/macro/reconciliation", "_blank")} />
                <CardContent style={{ padding: "0 0 8px 16px" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options[DataType.CONSUMPTION_SUMMARY]}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Increase in Consumption" />
                <Divider />
                <CardContent style={{ padding: 0, height: 258 }}>
                  <PerfectScrollbar>
                    <List data={options[DataType.INCREASE_IN_CONSUMPTION] || []} rightColor={colorTomatoRed.main} />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card className={classes.border}>
                <CardHeader title="Decrease in Consumption" />
                <Divider />
                <CardContent style={{ padding: 0, height: 258 }}>
                  <PerfectScrollbar>
                    <List data={options[DataType.DECREASE_IN_CONSUMPTION] || []} rightColor={colorLightGreen.main} />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>

            {/* Row 4 */}
            {renderMarkerEfficiency()}

            <Grid item xs={12} sm={6}>
              <Card className={classes.border}>
                <MuiToolbar>
                  <div className={classes.title}>
                    <CardHeader title="Low Efficiency" style={{ margin: 0, padding: 0 }} />
                  </div>
                  <div className={classes.spacer} />
                  <div className={classes.actions}>
                    <TextField
                      select
                      className={classes.textField}
                      value={category}
                      onChange={e => setCategory(e.target.value)}
                      SelectProps={{
                        MenuProps: {
                          className: classes.menu
                        }
                      }}>
                      {categoryList.map(category => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </MuiToolbar>
                <Divider />
                <CardContent style={{ padding: 0, height: 235 }}>
                  <PerfectScrollbar>
                    <Table
                      classes={{ table: classes.nowrapTable }}
                      columns={options[DataType.LOW_EFFICIENCY] && options[DataType.LOW_EFFICIENCY].columns}
                      rows={
                        options[DataType.LOW_EFFICIENCY] && options[DataType.LOW_EFFICIENCY].rows
                          ? options[DataType.LOW_EFFICIENCY].rows
                              .filter(row => row.category === category)
                              .sort((a, b) => a.markerEfficiency - b.markerEfficiency)
                              .slice(0, 6)
                          : []
                      }
                      cellStyle={({ column }) =>
                        column.dataKey === "efficiency"
                          ? {
                              color: colorTomatoRed.main
                            }
                          : {}
                      }
                      i18nKey="resources.dashboard.fields"
                    />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>

            {/* Row 5 */}
            {/* <Grid item xs={12} sm={6}>
              <Card className={classes.border}>
                {renderCardHeader("Roll Width Deviation", `Fabric: ${fabricUsed} m`, `/reports/macro/rollVariation`)}
                <Divider />
                <CardContent style={{ padding: 0, height: 235 }}>
                  <PerfectScrollbar>
                    <Table
                      classes={{ table: classes.nowrapTable }}
                      columns={options[DataType.WIDTH_DEVIATION] && options[DataType.WIDTH_DEVIATION].columns}
                      rows={options[DataType.WIDTH_DEVIATION] && options[DataType.WIDTH_DEVIATION].rows}
                      cellStyle={({ column }) =>
                        column.dataKey === "widthDeviation"
                          ? {
                              color: colorTomatoRed.main
                            }
                          : {}
                      }
                      i18nKey="resources.dashboard.fields"
                    />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card className={classes.border}>
                {renderCardHeader("Roll Length Variation", `Fabric: ${fabricUsed} m`, `/reports/macro/rollVariation`)}
                <Divider />
                <CardContent style={{ padding: 0, height: 235 }}>
                  <PerfectScrollbar>
                    <Table
                      classes={{ table: classes.nowrapTable }}
                      columns={options[DataType.LENGTH_VARIATION] && options[DataType.LENGTH_VARIATION].columns}
                      rows={options[DataType.LENGTH_VARIATION] && options[DataType.LENGTH_VARIATION].rows}
                      cellStyle={({ column }) =>
                        column.dataKey === "avgRollLength"
                          ? {
                              color: colorTomatoRed.main
                            }
                          : {}
                      }
                      i18nKey="resources.dashboard.fields"
                    />
                  </PerfectScrollbar>
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
